import React, { useState, useEffect } from 'react';
import './AnalysisDetails.css';

// API Configuration - Use relative URLs for same-domain requests
const API_BASE_URL = '';

const AnalysisDetails = ({ jobId, onClose, onBack }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [corrections, setCorrections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [correctionOptions, setCorrectionOptions] = useState(null);
  const [tscControls, setTscControls] = useState([]);
  const [editingCorrections, setEditingCorrections] = useState({});
  const [savingCorrections, setSavingCorrections] = useState({});
  const [correctionHistory, setCorrectionHistory] = useState([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState({});
  const [searchResults, setSearchResults] = useState({});

  // Load analysis details
  useEffect(() => {
    const fetchAnalysisDetails = async () => {
      try {
        setLoading(true);
        const sessionToken = localStorage.getItem('session_token');
        const response = await fetch(`${API_BASE_URL}/auditor/analysis/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch analysis details');
        }
        const data = await response.json();
        console.log('Analysis data received:', data);
        setAnalysisData(data.analysis_data);
        setCorrections(data.corrections || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisDetails();
  }, [jobId]);

  // Load correction options, TSC controls, and correction history
  useEffect(() => {
    const fetchCorrectionOptions = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/correction-options`);
        if (response.ok) {
          const options = await response.json();
          setCorrectionOptions(options);
        }
      } catch (err) {
        console.error('Failed to load correction options:', err);
      }
    };
    
    const fetchTSCControls = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/static/TSC_controls.json`);
        if (response.ok) {
          const controls = await response.json();
          setTscControls(controls);
        }
      } catch (err) {
        console.error('Failed to load TSC controls:', err);
      }
    };
    
    const fetchCorrectionHistory = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/corrections`);
        if (response.ok) {
          const history = await response.json();
          setCorrectionHistory(history);
        }
      } catch (err) {
        console.error('Failed to load correction history:', err);
      }
    };
    
    fetchCorrectionOptions();
    fetchTSCControls();
    fetchCorrectionHistory();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Fully aligned': return '#28a745';
      case 'Partially aligned': return '#ffc107';
      case 'Not aligned': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Fully aligned': return '✅';
      case 'Partially aligned': return '⚠️';
      case 'Not aligned': return '❌';
      default: return '❓';
    }
  };

  const handleEditCorrection = (controlId) => {
    const existingCorrection = corrections[controlId];
    const result = controlResults.find((_, index) => 
      (controlResults[index].control_text?.control_id || `CTRL_${String(index + 1).padStart(3, '0')}`) === controlId
    );
    
    setEditingCorrections(prev => ({
      ...prev,
      [controlId]: existingCorrection ? {
        // System-generated fields (read-only) - map from existing correction
        control_id: existingCorrection.tsc_control_id || '',
        control_text: existingCorrection.control_objective || '',
        internal_control_id: existingCorrection.internal_control_id || controlId,
        control_text_display: existingCorrection.control_objective || result?.control_text?.control_text || '',
        system_description: existingCorrection.ai_system_quote || result?.analysis?.quote || result?.matched_paragraph?.text || '',
        ai_reasoning: existingCorrection.ai_reasoning || result?.analysis?.explanation || '',
        tsc_category: existingCorrection.tsc_category || 'General',
        
        // Auditor-required fields - map from existing correction
        compliance_status: existingCorrection.auditor_corrected_decision || 'Not aligned',
        audit_reasoning: existingCorrection.auditor_reasoning || '',
        system_focus_area: existingCorrection.auditor_system_focus || '',
        
        // Correction requirements (structured) - map from existing correction
        correction_requirements: existingCorrection.correction_requirements || {
          immediate: '',
          ongoing: '',
          evidence_needed: ''
        },
        
        // Optional fields - map from existing correction
        confidence_level: existingCorrection.confidence_level || 5
      } : {
        // System-generated fields (read-only) - default values for new corrections
        control_id: '',
        control_text: '',
        internal_control_id: controlId,  // CTRL_001, CTRL_002, etc.
        control_text_display: result?.control_text?.control_text || '',
        system_description: result?.analysis?.quote || result?.matched_paragraph?.text || '',
        ai_reasoning: result?.analysis?.explanation || '',
        tsc_category: 'General',
        
        // Auditor-required fields - default values for new corrections
        compliance_status: 'Not aligned',
        audit_reasoning: '',
        system_focus_area: '',
        
        // Correction requirements (structured) - default values for new corrections
        correction_requirements: {
          immediate: '',
          ongoing: '',
          evidence_needed: ''
        },
        
        // Optional fields - default values for new corrections
        confidence_level: 5
      }
    }));
  };

  const handleCorrectionChange = (controlId, field, value) => {
    setEditingCorrections(prev => ({
      ...prev,
      [controlId]: {
        ...prev[controlId],
        [field]: value
      }
    }));
  };

  const handleSaveCorrection = async (controlId) => {
    const corr = editingCorrections[controlId];
    
    // Validation - match main page validation
    if (!corr.control_id || !corr.compliance_status || !corr.audit_reasoning || 
        !corr.system_focus_area || !corr.confidence_level) {
      alert('Please fill in all required fields (TSC Control ID, Compliance Status, Audit Reasoning, System Focus Area, and Confidence Level).');
      return;
    }

    try {
      setSavingCorrections(prev => ({ ...prev, [controlId]: true }));
      
      // Prepare payload to match main page structure
      const payload = {
        // Link to current analysis
        job_id: jobId,
        
        // System-generated fields
        internal_control_id: corr.internal_control_id,
        control_text: getControlTextById(corr.control_id),
        system_description: corr.system_description,
        ai_reasoning: corr.ai_reasoning,
        tsc_category: getTSCCategoryById(corr.control_id),
        
        // Auditor-required fields
        compliance_status: corr.compliance_status,
        audit_reasoning: corr.audit_reasoning,
        system_focus_area: corr.system_focus_area,
        
        // Correction requirements (structured)
        correction_requirements: corr.correction_requirements,
        
        // Optional fields
        confidence_level: corr.confidence_level
      };

      // Use the main corrections endpoint (not auditor-specific)
      const response = await fetch(`${API_BASE_URL}/corrections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save correction');
      }

      // Update local state - map the saved correction to backend field names for consistency
      const savedCorrection = {
        ...corr,
        // Map frontend field names to backend field names for consistency
        auditor_corrected_decision: corr.compliance_status,
        auditor_reasoning: corr.audit_reasoning,
        auditor_system_focus: corr.system_focus_area,
        tsc_control_id: corr.control_id,
        control_objective: corr.control_text,
        ai_system_quote: corr.system_description
      };
      
      setCorrections(prev => ({
        ...prev,
        [controlId]: savedCorrection
      }));

      // Clear editing state
      setEditingCorrections(prev => {
        const newState = { ...prev };
        delete newState[controlId];
        return newState;
      });

      alert('Correction saved successfully!');
      
      // Refresh correction history
      const historyResponse = await fetch(`${API_BASE_URL}/corrections`);
      if (historyResponse.ok) {
        const history = await historyResponse.json();
        setCorrectionHistory(history);
      }
      
    } catch (err) {
      alert(`Error saving correction: ${err.message}`);
    } finally {
      setSavingCorrections(prev => ({ ...prev, [controlId]: false }));
    }
  };

  const handleCancelEdit = (controlId) => {
    setEditingCorrections(prev => {
      const newState = { ...prev };
      delete newState[controlId];
      return newState;
    });
  };

  // Helper functions for correction history search
  const searchCorrections = (query, controlId) => {
    if (!query || query.length < 2) return [];
    
    const queryLower = query.toLowerCase();
    const results = correctionHistory
      .filter(correction => {
        // First priority: exact control_id match
        if (controlId && correction.control_id === controlId) return true;
        
        // Second priority: text similarity in reasoning
        const reasonLower = correction.reason?.toLowerCase() || '';
        const controlTextLower = correction.control_text?.toLowerCase() || '';
        
        return reasonLower.includes(queryLower) || 
               controlTextLower.includes(queryLower);
      })
      .map(correction => ({
        ...correction,
        relevance: calculateRelevance(queryLower, correction, controlId)
      }))
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, 5); // Top 5 results
    
    return results;
  };

  const calculateRelevance = (query, correction, controlId) => {
    let score = 0;
    
    // Exact control_id match gets highest priority
    if (controlId && correction.control_id === controlId) {
      score += 100;
    }
    
    // Text similarity in reasoning
    const reason = correction.reason?.toLowerCase() || '';
    if (reason.includes(query)) {
      score += 50;
      // Bonus for exact phrase match
      if (reason.includes(query)) score += 25;
    }
    
    // Text similarity in control text
    const controlText = correction.control_text?.toLowerCase() || '';
    if (controlText.includes(query)) {
      score += 30;
    }
    
    return score;
  };

  const handleAuditReasoningChange = (controlId, value) => {
    handleCorrectionChange(controlId, 'audit_reasoning', value);
    
    // Search for relevant corrections when user types
    if (value.length >= 2) {
      const control_id = editingCorrections[controlId]?.control_id;
      const results = searchCorrections(value, control_id);
      setSearchResults(prev => ({ ...prev, [controlId]: results }));
      setShowHistoryDropdown(prev => ({ ...prev, [controlId]: results.length > 0 }));
    } else {
      setSearchResults(prev => ({ ...prev, [controlId]: [] }));
      setShowHistoryDropdown(prev => ({ ...prev, [controlId]: false }));
    }
  };

  const selectHistoryItem = (controlId, historyItem) => {
    handleCorrectionChange(controlId, 'audit_reasoning', historyItem.reason);
    setShowHistoryDropdown(prev => ({ ...prev, [controlId]: false }));
    setSearchResults(prev => ({ ...prev, [controlId]: [] }));
  };

  // Helper to get control_text (objective) from TSC_controls.json by control_id
  const getControlTextById = (controlId) => {
    const ctrl = tscControls.find(ctrl => ctrl.control_id === controlId);
    return ctrl ? ctrl.control_objective : '';
  };

  // Helper to get tsc_category (ID + description) from TSC_controls.json by control_id
  const getTSCCategoryById = (controlId) => {
    const ctrl = tscControls.find(ctrl => ctrl.control_id === controlId);
    return ctrl ? `${ctrl.control_id} – ${ctrl.control_objective}` : '';
  };

  if (loading) {
    return (
      <div className="analysis-details-modal">
        <div className="analysis-details-content">
          <div className="analysis-details-header">
            <button className="back-button" onClick={onBack}>← Back to Analyses</button>
            <h2>Analysis Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading analysis details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analysis-details-modal">
        <div className="analysis-details-content">
          <div className="analysis-details-header">
            <button className="back-button" onClick={onBack}>← Back to Analyses</button>
            <h2>Analysis Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="error-container">
            <p className="error-message">⚠️ {error}</p>
            <button onClick={() => window.location.reload()} className="retry-button">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const results = analysisData?.results || {};
  const controlResults = results.results || [];
  
  console.log('Rendering with:', { analysisData, results, controlResults });

  return (
    <div className="analysis-details-modal">
      <div className="analysis-details-content">
        <div className="analysis-details-header">
          <button className="back-button" onClick={onBack}>← Back to Analyses</button>
          <h2>Analysis Details</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="analysis-details-body">
          <div className="analysis-summary">
            <h3>Analysis Summary</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-value">{controlResults.length}</span>
                <span className="stat-label">Total Controls</span>
              </div>
              <div className="stat">
                <span className="stat-value">{controlResults.filter(r => r.analysis?.match_status === 'Fully aligned').length}</span>
                <span className="stat-label">Fully Aligned</span>
              </div>
              <div className="stat">
                <span className="stat-value">{controlResults.filter(r => r.analysis?.match_status === 'Partially aligned').length}</span>
                <span className="stat-label">Partially Aligned</span>
              </div>
              <div className="stat">
                <span className="stat-value">{controlResults.filter(r => r.analysis?.match_status === 'Not aligned').length}</span>
                <span className="stat-label">Not Aligned</span>
              </div>
              <div className="stat">
                <span className="stat-value">{Object.keys(corrections).length}</span>
                <span className="stat-label">Corrections Made</span>
              </div>
            </div>
          </div>

          <div className="controls-section">
            <h3>Controls Analysis</h3>
            <div className="controls-list">
              {controlResults.map((control, index) => {
                const controlId = control.control_text?.control_id || `CTRL_${String(index + 1).padStart(3, '0')}`;
                const hasCorrection = corrections[controlId];
                const isEditing = editingCorrections[controlId];
                const isSaving = savingCorrections[controlId];

                return (
                  <div key={controlId} className={`control-item ${hasCorrection ? 'has-correction' : ''}`}>
                    <div className="control-header">
                      <div className="control-info">
                        <span className="control-id">{controlId}</span>
                        <span className="control-status" style={{ color: getStatusColor(control.analysis?.match_status) }}>
                          {getStatusIcon(control.analysis?.match_status)} {control.analysis?.match_status}
                        </span>
                        {hasCorrection && <span className="correction-indicator">✅ Corrected</span>}
                      </div>
                      <button
                        className="edit-correction-button"
                        onClick={() => handleEditCorrection(controlId)}
                        disabled={isEditing || isSaving}
                      >
                        {hasCorrection ? 'Edit Correction' : 'Add Correction'}
                      </button>
                    </div>

                    <div className="control-content">
                      <div className="control-text">
                        <strong>Control:</strong> {control.control_text?.control_text || 'N/A'}
                      </div>

                      {/* Analyzed System Text Section - Added for consistency with main page */}
                      <div className="matched-paragraph-section">
                        <h4>📄 Analyzed System Text</h4>
                        {control.analysis?.quote ? (
                          <div className="matched-content">
                            <div className="paragraph-text">
                              "{control.analysis.quote}"
                            </div>
                            {control.matched_paragraph?.section && (
                              <div className="section-info">
                                <strong>Source Section:</strong> {control.matched_paragraph.section}
                              </div>
                            )}
                            {control.similarity_score && (
                              <div className="confidence-info">
                                <strong>Match Confidence:</strong> {(control.similarity_score * 100).toFixed(1)}%
                              </div>
                            )}
                            <div className="analysis-context">
                              <small>💡 This is the specific text from your system description that the AI analyzed against the control requirement.</small>
                            </div>
                          </div>
                        ) : (
                          <div className="no-match-content">
                            <p className="no-match-text">❌ No specific system text was matched for this control</p>
                            <p className="no-match-explanation">
                              This control may require implementation that is not currently documented in the system description.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="ai-analysis">
                        <strong>🤖 AI Analysis & Reasoning:</strong> {control.analysis?.explanation || 'N/A'}
                      </div>
                    </div>

                    {isEditing && (
                      <div className="correction-form">
                        <h4>Audit Correction</h4>
                        
                        <div className="form-group">
                          <label>TSC Control ID *</label>
                          <select
                            value={isEditing.control_id}
                            onChange={(e) => handleCorrectionChange(controlId, 'control_id', e.target.value)}
                            required
                          >
                            <option value="">Select Control ID</option>
                            {tscControls.map(control => (
                              <option key={control.control_id} value={control.control_id}>
                                {control.control_id} - {control.control_objective}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>AI Original Decision</label>
                          <input
                            type="text"
                            value={control.analysis?.match_status || 'Unknown'} 
                            readOnly 
                            className="readonly-field"
                          />
                        </div>

                        <div className="form-group">
                          <label>AI Original Reasoning</label>
                          <textarea
                            value={control.analysis?.explanation || 'No explanation provided'} 
                            readOnly 
                            className="readonly-field"
                            rows={2}
                          />
                        </div>

                        <div className="form-group">
                          <label>Auditor Corrected Decision *</label>
                          <select
                            value={isEditing.compliance_status}
                            onChange={(e) => handleCorrectionChange(controlId, 'compliance_status', e.target.value)}
                            required
                          >
                            <option value="">Select Status</option>
                            <option value="Fully aligned">Fully aligned</option>
                            <option value="Partially aligned">Partially aligned</option>
                            <option value="Not aligned">Not aligned</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Audit Reasoning *</label>
                          <div className="reasoning-input-container">
                            <textarea
                              value={isEditing.audit_reasoning}
                              onChange={(e) => handleAuditReasoningChange(controlId, e.target.value)}
                              rows={3}
                              placeholder="Why do you disagree with AI's analysis?"
                              required
                            />
                            {showHistoryDropdown[controlId] && searchResults[controlId]?.length > 0 && (
                              <div className="search-results">
                                <div className="search-header">
                                  💡 Related corrections ({searchResults[controlId].length} found):
                                </div>
                                {searchResults[controlId].map((item, idx) => (
                                  <div key={idx} className="search-item" onClick={() => selectHistoryItem(controlId, item)}>
                                    <div className="search-reason">{item.reason}</div>
                                    <div className="search-meta">Control: {item.control_id} | Status: {item.match_label}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="form-group">
                          <label>System Focus Area *</label>
                          <textarea
                            value={isEditing.system_focus_area}
                            onChange={(e) => handleCorrectionChange(controlId, 'system_focus_area', e.target.value)}
                            rows={2}
                            placeholder="Which part of system description did you focus on?"
                            required
                          />
                        </div>

                        <div className="correction-requirements">
                          <label>Correction Requirements (for recommendations)</label>
                          
                          <div className="form-group">
                            <label>Immediate Actions (0-30 days)</label>
                            <textarea
                              value={isEditing.correction_requirements?.immediate || ''}
                              onChange={(e) => handleCorrectionChange(controlId, 'correction_requirements', {
                                ...isEditing.correction_requirements,
                                immediate: e.target.value
                              })}
                              rows={2}
                              placeholder="Actions that can be done immediately..."
                            />
                          </div>

                          <div className="form-group">
                            <label>Ongoing Processes (30-90 days)</label>
                            <textarea
                              value={isEditing.correction_requirements?.ongoing || ''}
                              onChange={(e) => handleCorrectionChange(controlId, 'correction_requirements', {
                                ...isEditing.correction_requirements,
                                ongoing: e.target.value
                              })}
                              rows={2}
                              placeholder="Systematic processes to establish..."
                            />
                          </div>

                          <div className="form-group">
                            <label>Evidence Required</label>
                            <textarea
                              value={isEditing.correction_requirements?.evidence_needed || ''}
                              onChange={(e) => handleCorrectionChange(controlId, 'correction_requirements', {
                                ...isEditing.correction_requirements,
                                evidence_needed: e.target.value
                              })}
                              rows={2}
                              placeholder="Specific documentation auditors expect..."
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Confidence Level: {isEditing.confidence_level}</label>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={isEditing.confidence_level}
                            onChange={(e) => handleCorrectionChange(controlId, 'confidence_level', parseInt(e.target.value))}
                            className="confidence-slider"
                          />
                          <div className="confidence-labels">
                            <span>Low (1)</span>
                            <span>High (10)</span>
                          </div>
                        </div>

                        <button 
                          type="button" 
                          className="submit-correction"
                          onClick={() => handleSaveCorrection(controlId)}
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : 'Save Correction'}
                        </button>
                      </div>
                    )}

                    {hasCorrection && !isEditing && (
                      <div className="existing-correction">
                        <h4>Audit Correction</h4>
                        <div className="correction-details">
                          <div><strong>Status:</strong> {hasCorrection.auditor_corrected_decision || hasCorrection.compliance_status}</div>
                          <div><strong>Audit Reasoning:</strong> {hasCorrection.auditor_reasoning || hasCorrection.audit_reasoning}</div>
                          <div><strong>System Focus Area:</strong> {hasCorrection.auditor_system_focus || hasCorrection.system_focus_area}</div>
                          <div><strong>Correction Requirements:</strong></div>
                          {hasCorrection.correction_requirements && typeof hasCorrection.correction_requirements === 'object' ? (
                            <div style={{ marginLeft: '20px' }}>
                              {hasCorrection.correction_requirements.immediate && (
                                <div><strong>• Immediate (0-30 days):</strong> {hasCorrection.correction_requirements.immediate}</div>
                              )}
                              {hasCorrection.correction_requirements.ongoing && (
                                <div><strong>• Ongoing (30-90 days):</strong> {hasCorrection.correction_requirements.ongoing}</div>
                              )}
                              {hasCorrection.correction_requirements.evidence_needed && (
                                <div><strong>• Evidence Required:</strong> {hasCorrection.correction_requirements.evidence_needed}</div>
                              )}
                            </div>
                          ) : (
                            <div style={{ marginLeft: '20px' }}>{hasCorrection.correction_requirements || 'No requirements specified'}</div>
                          )}
                          <div><strong>Confidence:</strong> {hasCorrection.confidence_level}/10</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisDetails; 